import { createAppSlice } from '..';
import {
  addObservation,
  archiveObservation as archiveApiObservation,
  archiveObservations as archiveApiObservations,
  createObservationCategory as createApiObservationCategory,
  deleteObservation as deleteApiObservation,
  deleteObservationCategory as deleteApiObservationCategory,
  deleteObservations as deleteApiObservations,
  getObservationCategories,
  getObservations,
  postFilesToObservation,
  removeBinary,
  restoreObservation as restoreApiObservation,
  restoreObservations as restoreApiObservations,
  updateObservation as updateApiObservation,
  updateObservationCategory as updateApiObservationCategory,
} from '../../api/api';
import { reportQueryKeys } from '../../api/queries/keys/reports';
import { workOrderQueryKeys } from '../../api/queries/keys/workOrders';
import { Task, TaskId, WorkOrderId } from '../../api/queries/workOrders';
import { queryClient, refreshQueries } from '../../api/queryClient';
import { OBSERVATION_CONSTANTS, ObservationStatus } from '../../constants/observationsConstants';
import { ItemWithPermissions } from '../../utils/permissions';
import { PartialWithRequired } from '../../utils/types';
import { ClusterId } from '../clusters';
import { Equipment, EquipmentId, deleteEquipment } from '../equipments';
import { Parcel, ParcelId, deleteMultiParcels, deleteParcel } from '../parcels';
import { DeviceSessionId, EquipmentSessionId } from '../sessions';
import { alerting, success } from '../snacks';
import { UserId } from '../users';
import { archiveObservationsOfAssets, observationsArchivedForAssets } from './archived';

export type ObservationCategoryId = number;
export type BasicObservationCategory = ItemWithPermissions & {
  id: ObservationCategoryId;
  label: string;
  color?: string;
  obs_count?: number;
  cluster_id?: never;
  observation_category_id?: never;
};
export type CustomObservationCategory = ItemWithPermissions & {
  id: ObservationCategoryId;
  label: string;
  color?: string;
  obs_count?: number;
  cluster_id: ClusterId;
  observation_category_id: ObservationCategoryId;
};
export type ObservationCategory = BasicObservationCategory | CustomObservationCategory;

export type ObservationBinaryId = string;
export type ObservationBinary = {
  id: ObservationBinaryId;
  title: string;
  /* Useful for observations not yet saved to the server */
  file?: File;
};

export type ObservationId = string;
export type Observation = ItemWithPermissions & {
  id: ObservationId;
  content: string;
  status: ObservationStatus;
  equipment_instance_id?: EquipmentId;
  equipment_instance?: Equipment;
  author_id: UserId;
  firstname: string;
  lastname: string;
  updater_firstname?: string;
  updater_lastname?: string;
  from_application: string;
  occurred_at: string;
  added_at: string;
  updated?: boolean;
  updated_at: string;
  last_updater_id: UserId;
  /* TODO: use device type when available */
  device_instance_id?: number;
  device_instance?: unknown;
  device_session_id?: DeviceSessionId;
  equipment_session_id?: EquipmentSessionId;
  /* TODO: use equipment session type when available */
  equipment_session?: unknown;
  location?: string;
  location_latitude?: number;
  location_longitude?: number;
  parcel_id?: ParcelId;
  parcel?: Parcel;
  task_id?: TaskId;
  task?: Task;
  work_order_id?: WorkOrderId;
  pending?: boolean;
  binaries?:
    | ObservationBinary[]
    | {
        pending?: number;
      };
  observation_category_id?: ObservationCategoryId;
};

export type ObservationAssetId = ParcelId | EquipmentSessionId | EquipmentId | TaskId;

type State = {
  categories: ObservationCategory[];
  list: Observation[];
  focusedObservationId?: ObservationId;
  isFetching: boolean;
};

const initialState: State = {
  categories: [],
  list: [],
  focusedObservationId: undefined,
  isFetching: false,
};

function invalidateObservationsReport() {
  void queryClient.invalidateQueries({ queryKey: reportQueryKeys.observations._def });
}

function invalidateWorkOrdersOf(observation: Partial<Observation>) {
  if (observation.task_id || observation.work_order_id) void refreshQueries(workOrderQueryKeys._def);
}

const generalSlice = createAppSlice({
  name: 'general',
  initialState,
  reducers: (create) => ({
    // Observation categories
    fetchObservationCategories: create.asyncThunk(
      async (_, { dispatch }) => {
        const categories = (await alerting(getObservationCategories, { dispatch })) as ObservationCategory[];
        return categories;
      },
      {
        fulfilled: (state, { payload }) => {
          state.categories = payload.filter((c) => !(!c.cluster_id && c.label === 'task'));
        },
      },
    ),
    createObservationCategory: create.asyncThunk(
      async (category: Partial<CustomObservationCategory>, { dispatch }) => {
        const newCategory = (await alerting(() => createApiObservationCategory(category), {
          dispatch,
        })) as CustomObservationCategory;
        success('Observations.add_category_snack', { dispatch });
        return newCategory;
      },
      {
        fulfilled: (state, { payload }) => {
          state.categories.push(payload);
        },
      },
    ),
    updateObservationCategory: create.asyncThunk(
      async (category: PartialWithRequired<ObservationCategory, 'id'>, { dispatch }) => {
        const updatedCategory = (await alerting(() => updateApiObservationCategory(category.id, category), {
          dispatch,
        })) as ObservationCategory;
        success('Observations.update_category_snack', { dispatch });
        return updatedCategory;
      },
      {
        fulfilled: (state, { payload }) => {
          state.categories = state.categories.map((c) => (c.id === payload.id ? payload : c));
        },
      },
    ),
    deleteObservationCategory: create.asyncThunk(
      async (categoryId: ObservationCategoryId, { dispatch }) => {
        await alerting(() => deleteApiObservationCategory(categoryId), { dispatch });
        success('Observations.delete_category_snack', { dispatch });
        return categoryId;
      },
      {
        fulfilled: (state, { payload }) => {
          state.categories = state.categories.filter((c) => c.id !== payload);
        },
      },
    ),

    // Observations
    fetchObservations: create.asyncThunk(
      async (params: `?${string}`, { dispatch }) => {
        const observations = (await alerting(() => getObservations(params), { dispatch })) as Observation[] | undefined;
        return observations;
      },
      {
        pending: (state) => {
          state.isFetching = true;
        },
        fulfilled: (state, { payload }) => {
          state.list = (payload || []).toReversed();
        },
        settled: (state) => {
          state.isFetching = false;
        },
      },
    ),
    createObservation: create.asyncThunk(
      async (
        payload: {
          params: Partial<Observation> & { files?: File[] };
          assetId: ObservationAssetId;
        },
        { dispatch },
      ) => {
        const observationPayload =
          payload.params.work_order_id && payload.params.task_id
            ? { ...payload.params, work_order_id: undefined }
            : payload.params;
        const observation = (await alerting(() => addObservation(observationPayload), { dispatch })) as Observation;
        let newFilesWithIds: ObservationBinary[] = [];
        if (payload.params.files?.length) {
          dispatch(
            addedObservation({
              observation: { ...observation, binaries: { pending: payload.params.files.length } },
              assetId: payload.assetId,
            }),
          );
          const filesPayload = await dispatch(
            addFilesToObservation({
              observationId: observation.id,
              assetId: payload.assetId,
              files: payload.params.files,
            }),
          );
          newFilesWithIds = (filesPayload.payload as { newFiles: ObservationBinary[] }).newFiles;
        } else {
          dispatch(
            addedObservation({
              observation,
              assetId: payload.assetId,
            }),
          );
        }
        invalidateWorkOrdersOf(payload.params);
        success('Observations.add_observation_snack', { dispatch });
        return {
          ...observation,
          work_order_id: payload.params.work_order_id,
          binaries: newFilesWithIds,
        };
      },
    ),
    updateObservation: create.asyncThunk(
      async (
        payload: {
          observation: PartialWithRequired<Observation, 'id'> & {
            newFiles?: File[];
            deletedFiles?: ObservationBinary[];
          };
          assetId: ObservationAssetId;
        },
        { dispatch },
      ) => {
        const observationPayload =
          payload.observation.work_order_id && payload.observation.task_id
            ? { ...payload.observation, work_order_id: undefined }
            : payload.observation;
        const removeGeoloc = !observationPayload.location_longitude && !observationPayload.location_latitude;
        const newObservation = (await alerting(
          () =>
            updateApiObservation(observationPayload.id, {
              ...observationPayload,
              location_latitude: removeGeoloc ? null : observationPayload.location_latitude,
              location_longitude: removeGeoloc ? null : observationPayload.location_longitude,
            }),
          { dispatch },
        )) as Observation;
        dispatch(
          updatedObservation({
            observation: newObservation,
            assetId: payload.assetId,
          }),
        );
        if (observationPayload.deletedFiles?.length) {
          await dispatch(
            deleteFilesFromObservation({
              observationId: newObservation.id,
              assetId: payload.assetId,
              deletedFiles: observationPayload.deletedFiles,
            }),
          );
        }
        let newFilesWithIds: ObservationBinary[] = [];
        if (observationPayload.newFiles?.length) {
          const filesPayload = await dispatch(
            addFilesToObservation({
              observationId: newObservation.id,
              assetId: payload.assetId,
              files: observationPayload.newFiles,
            }),
          );
          newFilesWithIds = (filesPayload.payload as { newFiles: ObservationBinary[] }).newFiles;
        }
        invalidateWorkOrdersOf(payload.observation);
        success('Observations.update_observation_snack', { dispatch });
        return {
          ...newObservation,
          work_order_id: payload.observation.work_order_id,
          binaries: [
            ...(Array.isArray(newObservation.binaries) ? newObservation.binaries : []),
            ...newFilesWithIds,
          ].filter((binary) => !observationPayload.deletedFiles?.some((deletedFile) => deletedFile.id === binary.id)),
        };
      },
    ),
    addFilesToObservation: create.asyncThunk(
      async (payload: { observationId: ObservationId; assetId: ObservationAssetId; files: File[] }, { dispatch }) => {
        const newFiles = (await alerting(() => postFilesToObservation(payload.observationId, payload.files), {
          dispatch,
        })) as ObservationBinary[];
        return {
          observationId: payload.observationId,
          assetId: payload.assetId,
          newFiles,
        };
      },
      {
        fulfilled: (state, { payload }) => {
          const obs = state.list.find((obs) => obs.id === payload.observationId);
          if (obs) {
            obs.binaries = [...(Array.isArray(obs.binaries) ? obs.binaries : []), ...payload.newFiles];
          }
        },
      },
    ),
    deleteFilesFromObservation: create.asyncThunk(
      async (
        payload: {
          observationId: ObservationId;
          assetId: ObservationAssetId;
          deletedFiles: ObservationBinary[];
        },
        { dispatch },
      ) => {
        const fileIds = payload.deletedFiles.map(({ id }) => id);
        await Promise.all(fileIds.map((id) => alerting(() => removeBinary(id), { dispatch })));
        return {
          observation: { id: payload.observationId },
          assetId: payload.assetId,
          deletedFileIds: fileIds,
        };
      },
      {
        fulfilled: (state, { payload }) => {
          const obs = state.list.find((o) => o.id === payload.observation.id);
          if (obs) {
            obs.binaries = (Array.isArray(obs.binaries) ? obs.binaries : []).filter(
              (binary) => !payload.deletedFileIds.includes(binary.id),
            );
            obs.updated = true;
          }
        },
      },
    ),
    deleteObservation: create.asyncThunk(
      async (
        payload: { observation: PartialWithRequired<Observation, 'id'>; assetId: ObservationAssetId },
        { dispatch },
      ) => {
        await alerting(() => deleteApiObservation(payload.observation), { dispatch });
        invalidateObservationsReport();
        // its deletion only knows its id, not the task or work order it was on
        void refreshQueries(workOrderQueryKeys._def);
        success('Observations.delete_observations_snack', { params: 1, dispatch });
        return payload;
      },
      {
        fulfilled: (state, { payload }) => {
          state.list = state.list.filter((o) => o.id !== payload.observation.id);
        },
      },
    ),
    deleteObservations: create.asyncThunk(
      async (observationIds: ObservationId[], { dispatch }) => {
        await alerting(() => deleteApiObservations(observationIds), { dispatch });
        invalidateObservationsReport();
        success('Observations.delete_observations_snack', { params: observationIds.length, dispatch });
        return observationIds;
      },
      {
        fulfilled: (state, { payload }) => {
          state.list = state.list.filter((o) => !payload.includes(o.id));
        },
      },
    ),
    archiveObservation: create.asyncThunk(
      async (payload: { observation: Observation; assetId: ObservationAssetId }, { dispatch }) => {
        const newObservation = (await alerting(() => archiveApiObservation(payload.observation.id), {
          dispatch,
        })) as Observation;
        dispatch(updatedObservation({ observation: newObservation, assetId: payload.assetId }));
        success('Observations.archive_observations_snack', { params: 1, dispatch });
      },
    ),
    archiveObservations: create.asyncThunk(
      async (observationIds: ObservationId[], { dispatch }) => {
        await alerting(() => archiveApiObservations(observationIds), { dispatch });
        invalidateObservationsReport();
        success('Observations.archive_observations_snack', { params: observationIds.length, dispatch });
        return observationIds;
      },
      {
        fulfilled: (state, { payload }) => {
          state.list = state.list.map((o) =>
            payload.includes(o.id) ? { ...o, status: OBSERVATION_CONSTANTS.STATUS.ARCHIVED } : o,
          );
        },
      },
    ),
    restoreObservation: create.asyncThunk(
      async (payload: { observation: Observation; assetId: ObservationAssetId }, { dispatch }) => {
        const newObservation = (await alerting(() => restoreApiObservation(payload.observation.id), {
          dispatch,
        })) as Observation;
        dispatch(updatedObservation({ observation: newObservation, assetId: payload.assetId }));
        success('Observations.restore_observations_snack', { params: 1, dispatch });
      },
    ),
    restoreObservations: create.asyncThunk(
      async (observationIds: ObservationId[], { dispatch }) => {
        await alerting(() => restoreApiObservations(observationIds), { dispatch });
        invalidateObservationsReport();
        success('Observations.restore_observations_snack', { params: observationIds.length, dispatch });
        return observationIds;
      },
      {
        fulfilled: (state, { payload }) => {
          state.list = state.list.map((o) =>
            payload.includes(o.id) ? { ...o, status: OBSERVATION_CONSTANTS.STATUS.AVAILABLE } : o,
          );
        },
      },
    ),

    addedObservation: create.reducer<{
      observation: Observation;
      assetId: ObservationAssetId;
    }>((state, { payload }) => {
      state.list = [payload.observation, ...state.list];
    }),
    updatedObservation: create.reducer<{
      observation: Observation;
      assetId: ObservationAssetId;
    }>((state, { payload }) => {
      state.list = state.list.map((o) => (o.id === payload.observation.id ? payload.observation : o));
    }),
    focusedObservation: create.reducer<ObservationId | null>((state, { payload }) => {
      state.focusedObservationId = payload || undefined;
    }),
  }),
  extraReducers: (builder) => {
    builder.addCase(deleteParcel.fulfilled, (state, { payload }) => {
      state.list = state.list.filter((o) => o.parcel_id !== payload);
    });

    builder.addCase(deleteMultiParcels.fulfilled, (state, { payload }) => {
      state.list = state.list.filter((o) => o.parcel_id === undefined || !payload.includes(o.parcel_id));
    });

    builder.addCase(deleteEquipment.fulfilled, (state, { payload }) => {
      state.list = state.list.filter((o) => o.equipment_instance_id !== payload);
    });

    builder.addCase(observationsArchivedForAssets, (state, { payload }) => {
      state.list = archiveObservationsOfAssets(state.list, payload);
    });
  },
  selectors: {
    selectState: (state) => state,
    selectObservationCategoriesFromState: (state) => state.categories,
    selectObservationList: (state) => state.list,
    selectFocusedObservationId: (state) => state.focusedObservationId,
    selectAreObservationsFetching: (state) => state.isFetching,
  },
});

export const {
  fetchObservationCategories,
  createObservationCategory,
  updateObservationCategory,
  deleteObservationCategory,

  fetchObservations,
  createObservation,
  updateObservation,
  addFilesToObservation,
  deleteFilesFromObservation,
  deleteObservation,
  deleteObservations,
  archiveObservation,
  archiveObservations,
  restoreObservation,
  restoreObservations,

  addedObservation,
  updatedObservation,
  focusedObservation,
} = generalSlice.actions;

export const {
  selectState,
  selectObservationCategoriesFromState,
  selectFocusedObservationId,
  selectAreObservationsFetching,
} = generalSlice.getSelectors((state: { observations: { general: State } }) => state.observations.general);

export default generalSlice.reducer;
